import { useState } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { dayLabel, formatDraftSlotLine, type DraftLineOptions, type Draft } from "@heraion/shared";

import { Button } from "../primitives/Button";
import { ChipChoiceRow } from "../primitives/ChipChoiceRow";
import { IconButton } from "../primitives/IconButton";
import { SelectMark } from "../primitives/SelectMark";
import type { UiTheme } from "../theme";
import { DayMenu, type DayMenuMode } from "./DayMenu";
import { groupLabels } from "./groupLabels";
import { RoundLens } from "./RoundLens";
import { SelectToggle } from "../primitives/SelectToggle";
import type { DaySelection } from "./useDaySelection";
import type { V3DayActions } from "./useV3DayActions";
import type { V3Lens, V3Navigation } from "./useV3Navigation";
import type { V3Styles } from "./v3Styles";

// One day (#336): its head with the day menu, the lens switch, then either
// one row per exercise with the schema line read from its values («Exercises»)
// or one round across the day («Per round», RoundLens), then «Add exercise».
// A row opens the exercise's round ladder. «Select» in the head (Exercises
// lens only) turns the rows into checkboxes for the selection bar (2B).
const LENSES: readonly V3Lens[] = ["exercises", "round"];

export function DayView({
  theme,
  styles,
  draft,
  nav,
  days,
  lineOptions,
  resolveName,
  onAddExercise,
  onAddToRound,
  selection,
}: {
  theme: UiTheme;
  styles: V3Styles;
  draft: Draft;
  nav: V3Navigation;
  days: V3DayActions;
  lineOptions: DraftLineOptions;
  resolveName: (id: string, snapshot?: string) => string;
  onAddExercise: () => void;
  /** Absent: the lens's off rows offer no «+». */
  onAddToRound?: (slotIdx: number, week: number) => void;
  /** Absent: no «Select», the rows only open. */
  selection?: DaySelection;
}) {
  const { t } = useTranslation();
  const [menu, setMenu] = useState<DayMenuMode>(null);
  const sIdx = nav.dayIdx;
  const slots = draft.sessions[sIdx]?.slots ?? [];
  const groups = groupLabels(draft, sIdx, t);
  const day = dayLabel(draft, sIdx);
  const lensLabel: Record<V3Lens, string> = {
    exercises: t("editorV3.lensExercises", { defaultValue: "Exercises" }),
    round: t("editorV3.lensRound", { defaultValue: "Per round" }),
  };
  return (
    <View style={styles.section}>
      <View style={styles.headRow}>
        <Text style={styles.headTitle} accessibilityRole="header">
          {day}
        </Text>
        {selection && nav.lens === "exercises" && slots.length > 0 ? (
          <SelectToggle
            theme={theme}
            active={selection.active}
            onPress={selection.active ? selection.stop : selection.start}
            day={day}
            a11yIdle={{ key: "editorV3.selectA11y", defaultValue: "Select exercises in {{day}}" }}
            a11yDone={{ key: "editorV3.selectDoneA11y", defaultValue: "Done selecting in {{day}}" }}
          />
        ) : null}
        <IconButton
          theme={theme}
          name="more"
          accessibilityLabel={t("editorV3.dayMenuA11y", { day, defaultValue: "{{day}}, day menu" })}
          onPress={() => setMenu("menu")}
        />
      </View>
      <DayMenu theme={theme} styles={styles} draft={draft} sIdx={sIdx} mode={menu} setMode={setMenu} days={days} />
      <ChipChoiceRow
        theme={theme}
        choices={LENSES}
        selected={nav.lens}
        onSelect={nav.setLens}
        label={(l) => lensLabel[l]}
        chipA11yLabel={(l) => lensLabel[l]}
        groupA11yLabel={t("editorV3.lensA11y", { defaultValue: "Read the day by" })}
      />
      {slots.length === 0 ? (
        <Text style={styles.empty}>
          {t("editorV3.dayEmpty", { day, defaultValue: "No exercises in {{day}} yet." })}
        </Text>
      ) : nav.lens === "round" ? (
        <RoundLens
          theme={theme}
          styles={styles}
          draft={draft}
          nav={nav}
          lineOptions={lineOptions}
          resolveName={resolveName}
          groups={groups}
          onAddToRound={onAddToRound}
        />
      ) : (
        <View style={styles.rows}>
          {slots.map((slot, slotIdx) => {
            const name = resolveName(slot.exerciseId, slot.exerciseName);
            const line = formatDraftSlotLine(slot, draft.cycleWeeks, lineOptions) ?? "";
            const tag = slot.group ? (groups.get(slot.group.id) ?? null) : null;
            const spoken = formatDraftSlotLine(slot, draft.cycleWeeks, lineOptions, true) ?? "";
            const a11y = [name, tag, spoken].filter(Boolean).join(", ");
            const body = (
              <>
                <View style={styles.rowTop}>
                  <Text style={styles.name} numberOfLines={2}>
                    {name}
                  </Text>
                  {tag ? <Text style={styles.tag}>{tag}</Text> : null}
                </View>
                <Text style={styles.line}>{line}</Text>
              </>
            );
            if (selection?.active) {
              const checked = selection.isSelected(slot.id);
              return (
                <Pressable
                  key={slot.id}
                  accessibilityRole="checkbox"
                  accessibilityState={{ checked }}
                  aria-checked={checked}
                  accessibilityLabel={a11y}
                  onPress={() => selection.toggle(slotIdx)}
                  style={[styles.row, styles.rowSelectable, tag ? styles.rowGrouped : null, checked && styles.rowChecked]}
                >
                  <SelectMark theme={theme} checked={checked} />
                  <View style={styles.rowBody}>{body}</View>
                </Pressable>
              );
            }
            return (
              <Pressable
                key={slot.id}
                accessibilityRole="button"
                accessibilityLabel={a11y}
                onPress={() => nav.openExercise(slot.id)}
                style={[styles.row, tag ? styles.rowGrouped : null]}
              >
                {body}
              </Pressable>
            );
          })}
        </View>
      )}
      <Button
        theme={theme}
        variant="secondary"
        icon="plus"
        label={t("mobile.logger.addExercise", { defaultValue: "Add exercise" })}
        onPress={onAddExercise}
      />
    </View>
  );
}
